import { useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import AlgebraActivityWrapper from '~/bootcamp/Activities/Algebra/Components/AlgebraActivityWrapper'
import SquareColorPicker, { SelectionColor } from '~/bootcamp/Activities/Algebra/Components/SquareColorPicker'

import usePowerColor from './usePowerColor'

function PowerColor({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const { clickableParts, formValid, onInputChange, handleCheckAnswer, goToNextQuestion, clearForm } = usePowerColor({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
  })

  const [activeColor, setActiveColor] = useState<SelectionColor>(SelectionColor.Blue)

  // `color` (which terms got matched by exponent) stays out — that is the answer.
  useEmitInstance(
    onInstanceChange,
    clickableParts?.length
      ? clickableParts
          .map(({ integer, operator, exponent, variable }, i) => {
            const sign = i > 0 && operator ? ` ${operator} ` : ''
            const coefficient = !exponent || integer > 1 ? `${integer}` : ''
            const powerPart = exponent ? `${variable}${exponent > 1 ? `^{${exponent}}` : ''}` : ''
            return `${sign}${coefficient}${powerPart}`
          })
          .join('')
      : null,
  )

  function getTextColorClass(selectedColor?: SelectionColor): string {
    if (answerState === false) return 'text-red'
    switch (selectedColor) {
      case SelectionColor.Purple:
        return 'text-purple'
      case SelectionColor.Blue:
        return 'text-blue-logo'
      case SelectionColor.Pink:
        return 'text-pink-logo'
      case SelectionColor.Orange:
        return 'text-orange-logo'
      default:
        return 'text-black'
    }
  }

  return (
    <ActivityWrapper>
      <AlgebraActivityWrapper className="flex flex-col">
        <div className="flex flex-row flex-wrap mb-2">
          {clickableParts &&
            clickableParts.map(({ integer, operator, exponent, variable, color }, i) => (
              <div
                className="transition duration-100 ease-in-out mx-1 cursor-pointer"
                key={`${integer}${i.toString()}`}
                onClick={onInputChange.bind(null, i, activeColor)}
              >
                <span className={`${getTextColorClass(color)} leading-10`}>
                  {operator && `${operator} `}
                  {!exponent || integer > 1 ? integer : ''}
                  {exponent && (
                    <>
                      <i>{variable}</i>
                      {exponent > 1 && <sup className="pl-1">{exponent}</sup>}
                    </>
                  )}
                </span>
              </div>
            ))}
        </div>
        <SquareColorPicker onBoxClick={setActiveColor} selectedColor={activeColor} />
      </AlgebraActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: clearForm,
          goToNextQuestionAction: goToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default PowerColor
